import React, { useEffect, useMemo, useState } from "react";
import { useSubscribeDetailsMutation } from "../redux/features/user/userApi";
import Navbar from "../Components/Navbar";

const PLAN_OPTIONS = ["Gold", "Diamond", "Platinum"]; // Backend expects capitalized names

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return iso ?? "-";
  }
};

const Subscribe = () => {
  const [selectedPlan, setSelectedPlan] = useState(PLAN_OPTIONS[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [fetchDetails, { data, isLoading, isError, error, isSuccess }] =
    useSubscribeDetailsMutation();
  const [fetchDetailsSummary] = useSubscribeDetailsMutation();
  const [allItems, setAllItems] = useState([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ⏰ Auto-refresh current date every hour to keep remaining days accurate
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchDetails(selectedPlan);
  }, [fetchDetails, selectedPlan]);

  useEffect(() => {
    let isCancelled = false;
    const loadAll = async () => {
      try {
        setIsSummaryLoading(true);
        const responses = await Promise.all(
          PLAN_OPTIONS.map((p) => fetchDetailsSummary(p))
        );
        if (isCancelled) return;
        const merged = responses.map((res) => res?.data?.data ?? []).flat();
        setAllItems(merged);
      } catch (_) {
        setAllItems([]);
      } finally {
        if (!isCancelled) setIsSummaryLoading(false);
      }
    };
    loadAll();
    return () => {
      isCancelled = true;
    };
  }, []);

  const items = useMemo(() => data?.data ?? [], [data]);

  // ✅ Calculate Remaining Days (endDate - currentDate)
  const computeRemainingDays = (endIso) => {
    try {
      if (!endIso) return "-";
      const end = new Date(endIso).setHours(0, 0, 0, 0);
      const now = new Date(currentDate).setHours(0, 0, 0, 0);

      if (isNaN(end)) return "-";

      const msInDay = 1000 * 60 * 60 * 24;
      const diff = Math.ceil((end - now) / msInDay);
      return diff < 0 ? 0 : diff;
    } catch (err) {
      return "-";
    }
  };

  const getRemainingDaysStyle = (days) => {
    if (days === "-" || days === null) return "text-gray-600";
    if (days === 0) return "text-red-600 font-semibold";
    if (days <= 500) return "text-orange-500 font-semibold";
    return "text-green-600 font-semibold";
  };

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const startMs = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const endMs = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;

    return items.filter((row) => {
      const matchesSearch =
        !term ||
        (() => {
          const haystack = [
            row?.planname,
            row?.userId,
            String(row?.roi),
            String(row?.term),
            String(row?.reward),
          ]
            .concat(row?.allocations ? Object.keys(row.allocations) : [])
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        })();

      const rowStart = row?.startdate
        ? new Date(row.startdate).getTime()
        : null;
      const inRange =
        (!startMs || (rowStart !== null && rowStart >= startMs)) &&
        (!endMs || (rowStart !== null && rowStart <= endMs));

      return matchesSearch && inRange;
    });
  }, [items, searchTerm, startDate, endDate]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDate, endDate]);

  const allocationTotals = useMemo(() => {
    const totals = {};
    for (const row of filteredItems) {
      if (!row?.allocations) continue;
      for (const [symbol, details] of Object.entries(row.allocations)) {
        const usd = Number(details?.usd) || 0;
        totals[symbol] = (totals[symbol] || 0) + usd;
      }
    }
    return totals;
  }, [filteredItems]);

  const summary = useMemo(() => {
    const mk = (arr) => {
      const active = arr.filter((r) => r?.Redeemed === false).length;
      const inactive = arr.filter((r) => r?.Redeemed === true).length;
      const count = arr.length;
      return { active, inactive, count };
    };
    const total = mk(allItems);
    const byPlan = PLAN_OPTIONS.reduce((acc, plan) => {
      const arr = allItems.filter(
        (r) => (r?.planname || "").toLowerCase() === plan.toLowerCase()
      );
      acc[plan] = mk(arr);
      return acc;
    }, {});
    return { total, byPlan };
  }, [allItems]);

  const exportCsv = (rows) => {
    const header = [
      "planname",
      "Redeemed",
      "planquote",
      "roi",
      "term",
      "reward",
      "startdate",
      "enddate",
      "remainingDays",
      "allocationsJSON",
      "userId",
      "_id",
    ];

    const csvRows = [header.join(",")];
    for (const r of rows) {
      const remaining = computeRemainingDays(r?.enddate);
      const line = [
        r?.planname ?? "",
        String(r?.Redeemed ?? ""),
        String(r?.planquote ?? ""),
        String(r?.roi ?? ""),
        String(r?.term ?? ""),
        String(r?.reward ?? ""),
        formatDate(r?.startdate) ?? "",
        formatDate(r?.enddate) ?? "",
        String(remaining),
        JSON.stringify(r?.allocations ?? {}),
        r?.userId ?? "",
        r?._id ?? "",
      ]
        .map((v) => {
          const s = String(v).replaceAll('"', '""');
          return `"${s}"`;
        })
        .join(",");
      csvRows.push(line);
    }

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribe_export_${selectedPlan.toLowerCase()}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Navbar />
      <div className="w-full px-4 md:px-8 lg:px-12 py-6 pt-20">
        {/* Summary Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white rounded-xl shadow-lg p-5">
            <div className="flex items-baseline justify-between">
              <div className="text-sm opacity-90">Total</div>
              <div className="text-xs opacity-80">Count</div>
            </div>
            <div className="mt-1 text-right font-bold text-2xl">
              {isSummaryLoading ? "-" : summary.total.count}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                Active
              </span>
              <span className="font-semibold text-lg">
                {isSummaryLoading ? "-" : summary.total.active}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                Inactive
              </span>
              <span className="font-semibold text-lg">
                {isSummaryLoading ? "-" : summary.total.inactive}
              </span>
            </div>
          </div>
          {PLAN_OPTIONS.map((p) => (
            <button
              key={p}
              onClick={() => setSelectedPlan(p)}
              className={`text-left rounded-xl p-5 shadow transition transform hover:-translate-y-0.5 hover:shadow-md border ${selectedPlan === p
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 bg-white"
                }`}
            >
              <div className="flex items-baseline justify-between">
                <div className="text-sm text-gray-600">{p}</div>
                <div className="text-xs text-gray-500">Count</div>
              </div>
              <div className="mt-1 text-right font-bold text-2xl">
                {isSummaryLoading ? "-" : summary.byPlan[p]?.count ?? 0}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Active
                </span>
                <span className="font-semibold text-lg">
                  {isSummaryLoading ? "-" : summary.byPlan[p]?.active ?? 0}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  Inactive
                </span>
                <span className="font-semibold text-lg">
                  {isSummaryLoading ? "-" : summary.byPlan[p]?.inactive ?? 0}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <h2 className="text-xl md:text-2xl font-semibold">
            Subscribe Details
          </h2>
          <div className="flex items-center gap-3 bg-white p-3 rounded shadow border border-gray-200">
            <label htmlFor="plan" className="text-sm md:text-base">
              Plan
            </label>
            <select
              id="plan"
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search & Date Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <input
            type="text"
            placeholder="Search plan, user, symbol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={() => exportCsv(filteredItems)}
              className="border rounded px-3 py-2 hover:bg-gray-50 bg-white shadow-sm"
            >
              Export Filtered
            </button>
            <button
              onClick={() => exportCsv(items)}
              className="border rounded px-3 py-2 hover:bg-gray-50 bg-white shadow-sm"
            >
              Export All
            </button>
          </div>
        </div>

        {/* Table */}
        {isLoading && <div className="text-sm">Loading...</div>}
        {isError && (
          <div className="text-sm text-red-600">
            {error?.data?.message || "Failed to load subscribe details."}
          </div>
        )}
        {isSuccess && items.length === 0 && (
          <div className="text-sm">No data found.</div>
        )}

        {filteredItems.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 border-b">Plan</th>
                  <th className="text-left p-3 border-b">Quote</th>
                  <th className="text-left p-3 border-b">ROI</th>
                  <th className="text-left p-3 border-b">Term</th>
                  <th className="text-left p-3 border-b">Reward</th>
                  <th className="text-left p-3 border-b">Start</th>
                  <th className="text-left p-3 border-b">End</th>
                  <th className="text-left p-3 border-b">Remaining Days</th>
                  <th className="text-left p-3 border-b">Status</th>
                  <th className="text-left p-3 border-b">Allocations</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((row) => {
                  const remaining = computeRemainingDays(row?.enddate);
                  return (
                    <tr key={row?._id} className="odd:bg-white even:bg-gray-50">
                      <td className="p-3 border-b">{row?.planname ?? "-"}</td>
                      <td className="p-3 border-b">${row?.planquote ?? "-"}</td>
                      <td className="p-3 border-b">{row?.roi ?? "-"}%</td>
                      <td className="p-3 border-b">{row?.term ?? "-"} Years</td>
                      <td className="p-3 border-b">
                        {row?.reward ?? "-"} KAIT
                      </td>
                      <td className="p-3 border-b">
                        {formatDate(row?.startdate)}
                      </td>
                      <td className="p-3 border-b">
                        {formatDate(row?.enddate)}
                      </td>
                      <td
                        className={`p-3 border-b ${getRemainingDaysStyle(
                          remaining
                        )}`}
                      >
                        {remaining} Days
                      </td>
                      <td className="p-3 border-b">
                        {row?.Redeemed === false ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-600"></span>
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-600"></span>
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="p-3 border-b align-top text-left">
                        {row?.allocations ? (
                          <div className="space-y-1">
                            {Object.entries(row.allocations).map(
                              ([symbol, details]) => (
                                <div
                                  key={symbol}
                                  className="flex items-center gap-2"
                                >
                                  <span className="font-medium uppercase ">
                                    {symbol}{" "}
                                  </span>
                                  <span className="text-gray-600 text-left">
                                    amt: {details?.amount ?? "-"}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 mt-4 rounded-b-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} entries
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Subscribe;
